import { BehaviorSubject } from 'rxjs';
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material';
import { UsersService } from 'app/users/users.service';
import { UUID } from 'angular2-uuid';
import { DesktopInstallDialogComponent } from '../shared/desktop-install-dialog.component';
declare var asperaSdk: any;

import { environment } from '../../environments/environment';

export interface AsperaBanner {
  visible: boolean;
  message: string;
  type: 'info' | 'warning';
  linkText?: string;
  action?: () => void;
}

const BTN_LABELS: { [key: string]: string } = {
  DISCONNECTED: 'Aspera Not Running',
  FAILED: 'Aspera Not Installed or Running'
};

@Injectable()
export class AsperaService implements OnDestroy {

  sdkLocation = environment.connectUrl;

  asperaStatus = 'unknown';
  launching = false;
  running = false;
  desktopMessage = 'Aspera for Desktop is selected. Aspera for Desktop is not installed or not open. '
  + 'Please install or open Aspera for Desktop';
  connectMessage = 'Aspera Connect is selected. Aspera Connect is not installed or open. However ' +
  'Aspera Connect is deprecated, please select Aspera for Desktop in user settings and install.';
  private sessionStarted = false;
  private transferListenerIds = new Map<Function, string>();
  private desktopInstallDialogRef: MatDialogRef<DesktopInstallDialogComponent> | null = null;

  private bannerSubject = new BehaviorSubject<AsperaBanner>(
    { visible: false, message: '', type: 'info', linkText: '' }
  );
  banner$ = this.bannerSubject.asObservable();

  private statusSubject = new BehaviorSubject<string>('unknown');
  status$ = this.statusSubject.asObservable();

  constructor(
    private usersService: UsersService,
    private dialog: MatDialog,
    private ngZone: NgZone) { }

  get useConnect(): boolean {
    return this.usersService.asperaClientMode !== 'desktop';
  }

  ngOnDestroy() {
    this.dismissDesktopInstallModal();
  }

  getActiveAsperaClientInfo(): Promise<{ name: string, version: string }>  {
    return asperaSdk.getInfo().then((info: any) => {
      const type = asperaSdk.currentTransferClient();
      const names: { [key: string]: string } = {
        connect: 'IBM Aspera Connect',
        desktop: 'IBM Aspera for Desktop'
      };
      const version = type === 'connect' ? info.connect.version : info.version;
      return { name: names[type] || type, version: version };
    });
  }

  buttonLabel(runningLabel: string): string {
    if (this.running) { return runningLabel; }
    return BTN_LABELS[this.asperaStatus] || runningLabel;
  }

  startSession(onFinish?: () => void) {
    if (this.launching) { return; }
    this.launching = true;
    if (!this.sessionStarted) {
      asperaSdk.registerStatusCallback((status: string) => { this.didUpdateStatus(status, onFinish); });
      try {
        asperaSdk.initSession({
          appId: 'monorail',
          connectSettings: { useConnect: this.useConnect, sdkLocation: this.sdkLocation, minVersion: '3.6.0' }
        });
        this.sessionStarted = true;
      } catch (e) {
        console.warn('Aspera session initialization failed:', e);
        this.launching = false;
        if (onFinish != null) { onFinish(); }
      }
    } else {
      this.launching = false;
      if (onFinish != null) { onFinish(); }
    }
  }

  didUpdateStatus(status: string, onFinish?: () => void) {
    this.asperaStatus = status;
    switch (status) {
    case 'RUNNING':
      this.running = true;
      // If the user installs/relaunches Desktop from the modal, close it.
      this.dismissDesktopInstallModal();
      this.launching = false;
      this.getActiveAsperaClientInfo().then(({ name, version }) => {
        this.bannerSubject.next({
          visible: true,
          message: `${this.useConnect ? 'Aspera Connect' : 'Aspera for Desktop'} is selected. Detected ${name}, version ${version}.`,
          type: 'info'
        });
      })
      .catch((error: any) => console.warn(`Aspera ${this.useConnect ?
        'Connect' : 'Desktop'} getActiveAsperaClientInfo failed:`, error.message, error.debugData));
      break;
    case 'FAILED':
      // Desktop not detected (not installed or not running when launched)
      this.running = false;
      asperaSdk.getInstallerInfo()
        .then((info: any) => {
          if (this.asperaStatus === 'FAILED' && !this.useConnect) {
            this.openDesktopInstallModal(info);
          }
        })
        .catch((error: any) => console.warn(`Aspera ${this.useConnect ?
          'Connect' : 'Desktop'} getInstallerInfo failed:`, error.message, error.debugData));
      this.bannerSubject.next({
        visible: true,
        message: this.useConnect ? this.connectMessage : this.desktopMessage,
        type: 'warning',
        linkText: 'Install or open Aspera for Desktop',
        action: () => this.showDesktopModal()
      });
      this.launching = false;
      break;
    case 'DISCONNECTED':
      // This status is only for if Running first and then not running / no connection
      this.running = false;
      if (!this.useConnect) {
        this.openDesktopInstallModal(null, 'disconnected');
        this.bannerSubject.next({
          visible: true,
          message: 'Aspera for Desktop is Disconnected or Closed.  Please relaunch or open Aspera for Desktop.',
          type: 'warning'
        });
      }
      this.launching = false;
      break;
    default:
      this.launching = false;
      break;
    }

    this.statusSubject.next(status);
    if (this.launching === false) {
      if (onFinish != null) { onFinish(); }
    }
  }

  selectFiles(allowMultiple: boolean = true): Promise<any> {
    return asperaSdk.showSelectFileDialog({ multiple: allowMultiple })
      .then((data: any) => data.dataTransfer.files.map((file: any) => {
        const path = file.name;
        file.path = path;
        file.name = this.fileNameFromPath(path);
        file.id = UUID.UUID();
        return file;
      }));
  }

  selectFolders(allowMultiple: boolean = true): Promise<any> {
    return asperaSdk.showSelectFolderDialog({ multiple: allowMultiple })
      .then((data: any) => {
        const folders = data.dataTransfer.files.map((file: any) => {
          const path = file.name;
          file.path = path;
          file.name = this.fileNameFromPath(path);
          file.id = UUID.UUID();
          return file;
        });
        // Connect can't use this
        if (!asperaSdk.hasCapability('readDirectory')) {
          return folders;
        }
        return Promise.all(folders.map((folder: any) =>
          asperaSdk.readDirectory({ path: folder.path })
            .then((res: any) => {
              folder.size = (res.entries || [])
                .reduce((sum: number, e: any) => sum + e.size, 0);
              return folder;
            })
            .catch(() => folder)
        ));
      });
  }

  fileNameFromPath(path: string): string {
    const splitPath = path.split(/[\\\//]/);
    return splitPath[splitPath.length - 1];
  }

  transferFiles(transferSpec: any): Promise<any> {
    transferSpec['authentication'] = 'token';
    transferSpec['create_dir'] = true;
    const settings = {allow_dialogs: false};
    return asperaSdk.startTransfer(transferSpec, settings);
  }

  supportsPerFileProgress(): boolean {
    return asperaSdk.hasCapability('getFilesList');
  }

  getTransferFiles(transferId: string): Promise<any> {
    return asperaSdk.getFilesList(transferId);
  }

  // Open already installed Desktop app.
  launchDesktop() {
    asperaSdk.launch();
  }

  showDesktopModal() {
    if (this.asperaStatus === 'DISCONNECTED') {
      this.openDesktopInstallModal(null, 'disconnected');
    } else if (this.asperaStatus === 'FAILED') {
      asperaSdk.getInstallerInfo()
        .then((info: any) => this.openDesktopInstallModal(info))
        .catch((error: any) => console.warn('Aspera getInstallerInfo failed:', error.message, error.debugData));
    }
  }

  addTransferListener(listener: (eventType: string, transferInfo: any) => void) {
    const wrapper = (activity: any) => listener('transfer', activity);
    const id = asperaSdk.registerActivityCallback(wrapper);
    this.transferListenerIds.set(listener, id);
  }

  removeTransferListener(listener: (eventType: string, transferInfo: any) => void) {
    const id = this.transferListenerIds.get(listener);
    if (id != null) {
      asperaSdk.deregisterActivityCallback(id);
      this.transferListenerIds.delete(listener);
    }
  }

  private openDesktopInstallModal(info: any, mode: string = 'install') {
    // Guard against stacking a second modal if the status re-fires while open.
    if (this.desktopInstallDialogRef != null) { return; }
    this.ngZone.run(() => {
      this.desktopInstallDialogRef = this.dialog.open(DesktopInstallDialogComponent, {
        data: { mode: mode, info: info, onLaunch: () => this.launchDesktop() },
        width: '600px',
        panelClass: 'desktop-install-panel',
        position: { top: '0px' },
        disableClose: true
      });
      this.desktopInstallDialogRef.afterClosed().subscribe(() => {
        this.desktopInstallDialogRef = null;
      });
    });
  }

  private dismissDesktopInstallModal() {
    if (this.desktopInstallDialogRef != null) {
      const ref = this.desktopInstallDialogRef;
      this.desktopInstallDialogRef = null;
      this.ngZone.run(() => ref.close());
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/aspera/aspera.service.ts